import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { TenancyService } from '../tenancy.service';
import { AuthService } from '@core/services/common';
import { ApiService } from '@core/services/api';
import { PARAMETRO_DE_ENTRADA } from '@core/services/api/routes/accountant/portfolio.route';

@Injectable({
  providedIn: 'root',
})
export class CheckRedirectGuard {
  private tenancyService = inject(TenancyService);
  private auth = inject(AuthService);
  private api = inject(ApiService);

  canActivate(
    route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot
  ): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    // A ÁREA DO CONTADOR TEM RAIZ PRÓPRIA, e este ramo vem antes do
    // `!subdomain` de propósito.
    //
    // No host do contador, `makeSubdomain()` devolve `undefined` — o host é
    // reservado (`dominiosReservados()`) —, o que é INDISTINGUÍVEL do
    // backoffice central para a linha de baixo. Sem este ramo, três coisas
    // acontecem em produção e nenhuma delas em QA (onde `/` já é o central,
    // então o defeito é invisível a qualquer teste manual antes do deploy):
    //
    //   1. o contador que digita `contador.maisgestao.com.br` cai no login do
    //      BACKOFFICE;
    //   2. o 401 do `ApiInterceptor` — que faz `logout()` e manda para `/` —
    //      termina a sessão expirada da carteira no backoffice, com o toast
    //      "Sua sessão expirou" na frente;
    //   3. o mesmo vale para qualquer link para a raiz.
    if (this.tenancyService.isAccountant) {
      window.location.href = `${this.tenancyService.accountantAppUrl()}contador`;
      return false;
    }

    const subdomain = this.tenancyService.makeSubdomain(window.location.hostname);
    if (!subdomain) {
      // A raiz é a entrada pública; o painel central exige o caminho /admin.
      window.location.href = 'https://maisgestao.com.br/entrar';
      return false;
    }
    const appUrl = this.tenancyService.appUrl(subdomain);

    const codigoDoContador = route.queryParams[PARAMETRO_DE_ENTRADA];
    if (codigoDoContador) {
      // Mesmo desenho do bilhete de suporte: limpa o endereço ANTES da troca.
      // O que chegou aqui é um CÓDIGO de uso único com 2 minutos de vida — não
      // um token —, e quem o troca por sessão é este lado, o do tenant.
      this.limparEndereco();

      return this.api.accountantAccess.exchange(codigoDoContador).pipe(
        tap(({ token }) => this.auth.loginByAccess(token)),
        map(() => {
          window.location.href = `${appUrl}painel`;
          return false;
        }),
        catchError(() => {
          window.location.href = `${appUrl}painel/auth/login`;
          return of(false);
        }),
      );
    }

    const { ticket } = route.queryParams;
    if (ticket) {
      // A limpeza vem ANTES do resgate, de propósito: se o resgate falhar, o
      // bilhete já saiu da barra de endereços e do histórico do navegador.
      this.limparEndereco();

      return this.api.support.redeem(ticket).pipe(
        tap(({ token }) => this.auth.loginByAccess(token)),
        map(() => {
          window.location.href = `${appUrl}painel`;
          return false;
        }),
        catchError(() => {
          // Não existe rota `login` na raiz do tenant — o login real do
          // painel fica sob `painel/auth/login` (`tenant-routing.module.ts`,
          // que importa `AuthModule` em `auth`). `${appUrl}login` caía no
          // curinga da raiz, que redireciona de novo para `painel`, que cai
          // no curinga do tenant e termina no módulo de erro.
          window.location.href = `${appUrl}painel/auth/login`;
          return of(false);
        }),
      );
    }

    window.location.href = `${appUrl}painel`;
    return false;
  }

  /**
   * `Router.navigate({ replaceUrl: true })` NÃO alcança esta saída: o guard
   * sai por `window.location.href` — navegação de página inteira, fora do
   * Router —, e o Angular não controla esta entrada de histórico.
   * `history.replaceState` é o único mecanismo que chega até a barra de
   * endereços antes dessa navegação.
   */
  private limparEndereco(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete('ticket');
    url.searchParams.delete(PARAMETRO_DE_ENTRADA);
    window.history.replaceState({}, '', url.toString());
  }
}
